import { useAuth0 } from '@auth0/auth0-react';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import useCreateParliamentLiveFilters from 'components/Dashboard/ParliamentLive/useCreateParliamentLiveFilters';
import useGetRealTimeEvents from 'components/Dashboard/ParliamentLive/useGetRealtimeEvents';
import time from 'utils/time';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import { store } from 'components/Store';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import AIConnectorButton from 'components/Settings/Integrations/AIConnector';
import integrationsCondition from 'components/Settings/Integrations/integrationsCondition';

const DashboardSubnav = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { team } = state;

  const { generalApiCall } = useGeneralApiCall();
  const { getRealTimeEvents } = useGetRealTimeEvents();
  const { createParliamentLiveFilters } = useCreateParliamentLiveFilters();

  const { updateState } = props ?? {};
  const { user } = useAuth0();
  const [results, setResults] = useState([]);
  const [remarks, setRemarks] = useState([]);
  const [realtimeEvents, setRealtimeEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [animationState, setAnimationState] = useState('enter');
  const [currentIndex, setCurrentIndex] = useState(0);

  const isMobile = useIsMobile();

  const getUKGreeting = () => {
    const nowUK = time();
    const hour = nowUK.hour();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const initialCall = useRef(null);
  initialCall.current = async ({ source }) => {
    try {
      setIsLoading(true);
      const { parliamentLiveFilters } = createParliamentLiveFilters({});
      const realTimeEvents = await getRealTimeEvents({ source });
      setRealtimeEvents(realTimeEvents || []);
      const requestProperties = {
        doNotSearchTitle: true,
        keywordListId: null,
        pageNumber: 1,
        pageSize: 1000,
        filters: parliamentLiveFilters,
        sort: { field: 'title', order: 'DESC' },
        type: 'RealtimeUtterance',
      };
      const pathname = `/api/search/query-by-keyword-list-v2`;
      const results = await generalApiCall({
        pathname,
        method: 'post',
        needsAuthentication: true,
        notShowErrorMessage: true,
        requestSource: source,
        requestProperties: {
          ...requestProperties,
          collapseField: 'parliamentEventId',
          collapseSort: { field: 'dateTime', order: 'DESC' },
          doNotIncludeCollapsedHits: true,
        },
      });
      if (!!results) {
        const remarks = await generalApiCall({
          pathname,
          method: 'post',
          needsAuthentication: true,
          notShowErrorMessage: true,
          requestSource: source,
          requestProperties,
        });
        if (!!remarks) {
          setRemarks(remarks?.hits);
        }
        const finalResults = results?.hits?.filter((hit) => {
          return !!realTimeEvents?.find((event) => event?.id === hit?.eventId && !event?.eventHasEnded);
        });

        setResults(finalResults);
        setIsLoading(false);
      }
    } catch (error) {}
  };

  useEffect(() => {
    const source = new AbortController();
    initialCall.current({ source });
    return () => {
      source.abort('DashboardSubnav cancelled by the user');
    };
  }, [team]);

  const intervalRef = useRef(null);
  // Track the last manually set index to allow interval to continue from there
  const lastManualIndexRef = useRef(null);

  useEffect(() => {
    // Helper to clear interval
    const clear = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    if (results.length > 1 && remarks.length > 0) {
      intervalRef.current = setInterval(() => {
        setAnimationState('exit');
        setTimeout(() => {
          setCurrentIndex((prev) => {
            // If a manual index was set, continue from there
            if (lastManualIndexRef.current !== null) {
              const nextIndex = (lastManualIndexRef.current + 1) % results.length;
              lastManualIndexRef.current = null;
              return nextIndex;
            }
            return (prev + 1) % results.length;
          });
          setAnimationState('enter');
        }, 500);
      }, 10000);
    } else {
      clear();
    }
    return clear;
  }, [results, remarks]);

  // Pass a custom setCurrentIndex that tracks manual changes and triggers animation
  const handleSetCurrentIndex = (index) => {
    lastManualIndexRef.current = index;
    setAnimationState('exit');
    setTimeout(() => {
      setCurrentIndex(index);
      setAnimationState('enter');
    }, 500);
  };

  return (
    <div
      className={`bg-white monitor-items-bar inbox-items-bar user-select-none flex-centered  justify-content-center`}
    >
      <div className='mx-auto main-content-wrapper flex-centered justify-content-between resize-elements-container'>
        <div className='side-left-element pl-lg-5 pl-4 '>
          <div className='position-relative mr-3'>
            <p className={`mb-0 title-h4 hidden-lines hidden-one-line font-weight-bold`}>
              {getUKGreeting()}, {user?.given_name}
            </p>
          </div>
        </div>
        <div
          className={`${
            isMobile ? '' : 'main-content'
          } flex-centered position-relative filters-general-container justify-content-end pr-xl-5 px-md-4 px-3`}
          style={{ minHeight: '36px' }}
        >
          {!isLoading && (
            <>
              <div className='flex-centered'>
                <div
                  className='flex-grow-1 d-none d-lg-flex align-items-center justify-content-center flex-column'
                  style={{ height: '36px', overflowY: 'hidden' }}
                >
                  {results.length === 0 && (
                    <DashboardLiveEvent
                      result={null}
                      remarks={remarks}
                      realtimeEvents={realtimeEvents}
                      updateState={updateState}
                    />
                  )}
                  {results.length === 1 && (
                    <DashboardLiveEvent
                      result={results[0]}
                      remarks={remarks}
                      realtimeEvents={realtimeEvents}
                      updateState={updateState}
                    />
                  )}
                  {results.length > 1 && (
                    <DashboardLiveEvent
                      animationState={animationState}
                      result={results[currentIndex]}
                      remarks={remarks}
                      realtimeEvents={realtimeEvents}
                      updateState={updateState}
                    />
                  )}
                </div>
                {results?.length >= 0 && (
                  <DashboardSubnavSeparator
                    results={results}
                    currentIndexState={{ currentIndex, setCurrentIndex: handleSetCurrentIndex }}
                    remarks={remarks}
                  />
                )}
                {integrationsCondition ? (
                  <AIConnectorButton />
                ) : (
                  <Link
                    to={`/settings/alerts`}
                    className='rounded-pill reset-link border general-button px-3 specific-y-padding-5 flex-centered action-state-hover light-70-text nowrap-item'
                  >
                    <span className='icon-alert-medium mr-2 title-h4' />{' '}
                    <span>{isMobile ? 'Alerts' : 'Manage alerts'}</span>
                  </Link>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const DashboardLiveEvent = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { activePlan } = state;

  const { enableLiveTranscription } = activePlan ?? {};

  const { animationState = 'enter', result, remarks, realtimeEvents, updateState } = props;
  const { title, sourceUrl, eventId, contentUrl } = result ?? {};
  const { updatePanelState, setUpdatePanelState } = updateState ?? {};
  const numberOfRemarks = remarks?.filter((item) => item?.eventId === eventId)?.length;

  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile(1280);
  const { parliamentIsNotLive } = useCreateParliamentLiveFilters();

  const parliamentIsNotLiveCondition = parliamentIsNotLive({ realtimeEvents });

  const createUrl = (url) => {
    try {
      const itemUrl = new URL(url);
      const { pathname, origin } = itemUrl;
      return `${origin}${pathname}`;
    } catch (error) {
      return '';
    }
  };

  return result !== null ? (
    <p
      onClick={() => {
        if (isMobile) {
          navigate(contentUrl);
        }
      }}
      className={`mb-0 ${
        numberOfRemarks > 0 ? `live-event ${isMobile ? 'general-link pointer' : ''}` : ''
      } flex-centered w-100 pl-4 ${animationState} `}
    >
      <span className='flex-centered nowrap-item'>
        <span className={`tag-keyword-list-live`}>
          <span className='element-record' />
        </span>
        {numberOfRemarks > 0 && <span className='font-weight-bold mr-1'>Live now:</span>}{' '}
      </span>
      <span className='hidden-lines hidden-one-line'>
        {numberOfRemarks > 0 ? title : 'Parliament is live but there are no relevant remarks to show you'}
      </span>
      {numberOfRemarks > 0 && (
        <button
          className='d-none d-xl-inline-block rounded-pill border general-button px-3 specific-y-padding-5 text-light-blue flex-centered hovered-button ml-3 nowrap-item'
          onClick={() => {
            const searchParams = new URLSearchParams(location?.search);
            searchParams.set('topic-id', 'all');
            const timesClicked =
              eventId !== updatePanelState?.eventId || !updatePanelState?.timesClicked?.[contentUrl]
                ? 0
                : updatePanelState?.timesClicked?.[contentUrl];
            const newNumber = timesClicked + 1;
            setUpdatePanelState({
              open: true,
              eventId,
              eventUrl: contentUrl,
              eventTitle: title,
              eventRemarks: numberOfRemarks,
              eventSourceUrl: createUrl(sourceUrl),
              timesClicked: { ...timesClicked, [contentUrl]: newNumber },
              highlightUpdate: newNumber > 1,
            });
            navigate({ search: searchParams?.toString() });
          }}
        >
          <span className='icon-quotes text-light-blue mr-2 paragraph-p5 d-inline-block-centered' />
          {enableLiveTranscription ? 'See' : ''} {numberOfRemarks} relevant remark{numberOfRemarks === 1 ? '' : 's'}
        </button>
      )}
    </p>
  ) : (
    <>
      {parliamentIsNotLiveCondition ? (
        <p className='mb-0'>
          <span className='dot-11 bg-main-blue-30 specific-right-margin-8'></span> There are no live events in
          Parliament right now
        </p>
      ) : (
        <p className={`mb-0 live-event flex-centered w-100 pl-4`}>
          <span className='flex-centered nowrap-item'>
            <span className={`tag-keyword-list-live`}>
              <span className='element-record' />
            </span>
          </span>{' '}
          <span className='hidden-lines hidden-one-line'>
            We’re listening to Parliament and will show relevant remarks here as they happen
          </span>
        </p>
      )}
    </>
  );
};

const DashboardSubnavSeparator = (props) => {
  const { results, currentIndexState, remarks } = props;
  const { currentIndex, setCurrentIndex } = currentIndexState ?? {};
  const total = results.length;
  const maxLines = total === 1 ? 1 : total === 2 ? 2 : 3;
  const maxHeight = 30;
  const marginValue = 4;

  let startIndex = 0;
  if (total > 3) {
    startIndex = Math.floor(currentIndex / 3) * 3;
  }
  const getIndex = (line) => {
    if (total <= 3) return line;
    return (startIndex + line) % total;
  };
  const isClickable = total > 1 && remarks.length > 0 && results.length > 1;
  return (
    <div
      className={`d-none d-lg-flex flex-column align-items-center justify-content-between ${
        isClickable ? ' pointer' : ''
      }`}
      style={{ height: `${maxHeight}px` }}
      onClick={() => isClickable && setCurrentIndex && setCurrentIndex((currentIndex + 1) % total)}
    >
      {[...Array(maxLines)].map((_, line) => {
        const index = getIndex(line);
        const isActive = isClickable && index === currentIndex;
        return (
          <div className={`px-3`} key={line}>
            <div
              className={`bg-main-blue${isActive ? '' : '-20'}`}
              style={{
                height: `${
                  !isClickable
                    ? maxHeight / maxLines
                    : total === 1
                      ? maxHeight
                      : (maxHeight - (total === 2 ? marginValue : marginValue * 2)) / maxLines
                }px`,
                width: '2px',
                transition: 'background 0.2s',
              }}
            />
          </div>
        );
      })}
    </div>
  );
};

export default DashboardSubnav;
